import type { Moneda } from "./format";

/**
 * Cliente único de la API. Todo fetch del frontend pasa por acá.
 * URLs SIEMPRE absolutas (Safari rechaza relativas en algunos casos).
 */
export const API = window.location.origin;

export type Rol = "admin" | "fiduciante" | "contador" | "escribano";

export interface Membresia {
  fideicomiso_id: number;
  fideicomiso_nombre: string;
  rol: Rol;
}

export interface Usuario {
  id: number;
  email: string;
  nombre: string;
  activo: boolean;
  membresias: Membresia[];
  /** Admin de algún desarrollo (o no hay ninguno todavía): puede crear uno nuevo. */
  puede_crear_desarrollo: boolean;
  /** Entró con una contraseña temporal: hasta elegir la suya no puede usar nada más (la API responde 403). */
  debe_cambiar_password: boolean;
  /** El admin está viendo la app como este fiduciante (sólo lectura). */
  vista: VistaComo | null;
}

/** Un fiduciante como el que el admin puede ver la app (GET /fideicomisos/{fid}/ver-como). */
export interface VerComoOpcion {
  fiduciante_id: number;
  nombre: string;
}

export interface VistaComo {
  fiduciante_id: number;
  fiduciante_nombre: string;
  admin_nombre: string;
}

export interface Fideicomiso {
  id: number;
  nombre: string;
  cuit: string | null;
  direccion: string | null;
  fecha_constitucion: string | null;
  fee_porcentaje: string;
  banco: string | null;
  cbu_alias_ars: string | null;
  cuenta_usd: string | null;
  fiduciario_nombre: string | null;
  fiduciario_cuit: string | null;
}

export type ClavePaso = "fideicomiso" | "fiduciario" | "fiduciantes" | "ubicacion" | "compra" | "documentacion" | "unidades" | "presupuesto" | "cronograma";

/** Las tarjetas del Inicio: el desarrollo es el fideicomiso más el terreno; la obra viene después. */
export type GrupoPaso = "fideicomiso" | "terreno" | "obra";

export interface PasoPuestaEnMarcha {
  clave: ClavePaso;
  grupo: GrupoPaso;
  completo: boolean;
  detalle: string | null; // qué falta; null si está completo
}

/** Checklist del Inicio del admin (`GET /puesta-en-marcha`). */
export interface PuestaEnMarcha {
  pasos: PasoPuestaEnMarcha[];
  completos: number;
  total: number;
  completa: boolean;
}

export type EstadoMiembro = "activo" | "invitacion_pendiente" | "primer_ingreso" | "temporal_vencida" | "desactivado";

export interface Miembro {
  id: number;
  nombre: string;
  email: string;
  rol: Rol;
  activo: boolean;
  estado: EstadoMiembro;
  /** Con rol fiduciante: la ficha vinculada en este desarrollo (null = fiduciante suelto, de antes). */
  fiduciante: { id: number; nombre: string } | null;
}

/** Sale una sola vez (en la DB queda el hash): el admin la copia y se la dicta o manda. */
export interface PasswordTemporal {
  password: string;
  vence_en: string;
}

export interface AltaMiembro {
  usuario: Miembro;
  password_temporal: PasswordTemporal | null;
}

export interface InvitacionInfo {
  nombre: string;
  email: string;
}

export type TipoAviso = "llamado_nuevo" | "llamado_recordatorio" | "novedad" | "respaldo_fallido" | "respaldo_ausente";
export type EstadoAviso = "pendiente" | "enviado" | "fallido" | "descartado";

export type CanalAviso = "mail" | "push";

export interface PreferenciasAvisos {
  avisar_llamados: boolean;
  avisar_novedades: boolean;
  push_llamados: boolean;
  push_novedades: boolean;
}

export interface Aviso {
  id: number;
  creado_en: string;
  enviado_en: string | null;
  tipo: TipoAviso;
  estado: EstadoAviso;
  canal: CanalAviso;
  email: string | null;
  nombre: string;
  asunto: string;
  intentos: number;
  ultimo_error: string | null;
}

export interface ClavePublica {
  clave: string;
}

export interface DispositivoNuevo {
  endpoint: string;
  p256dh: string;
  auth: string;
  nombre: string;
}

export interface DispositivoPush {
  id: number;
  plataforma: "web";
  endpoint: string;
  nombre: string;
  creado_en: string;
  usado_en: string | null;
}

export type TipoFiduciante = "humana" | "juridica";
/** A: inversor (la plata de las ventas es suya). B: se queda con m² y pone su parte siempre. */
export type ClaseFiduciante = "A" | "B";
export type EstadoPortal = "sin_acceso" | EstadoMiembro;

/** Lo que usan las listas de fiduciantes (Aportes, Grupos, Participación…): sin datos personales. */
export interface FiduciantePublico {
  id: number;
  nombre: string;
  tipo: TipoFiduciante;
  porcentaje_vigente: string | null; // suma de sus clases
  clases_vigentes: ClaseFiduciante[];
}

/** Ficha completa (admin y contador). */
export interface Fiduciante extends FiduciantePublico {
  documento: string | null;
  email: string | null;
  telefono: string | null;
  domicilio: string | null;
  notas: string | null;
  usuario_id: number | null;
  estado_portal: EstadoPortal;
}

export interface ItemDistribucion {
  fiduciante_id: number;
  nombre: string;
  clase: ClaseFiduciante;
  porcentaje: string; // A y B, sobre el total
}

export interface Distribucion {
  id: number;
  vigente_desde: string;
  nota: string | null;
  vigente: boolean;
  /** Suma exactamente 100. Una incompleta (borrador) se guarda pero no rige. */
  completa: boolean;
  suma: string;
  suma_a: string;
  suma_b: string;
  editable: boolean;
  items: ItemDistribucion[];
  /** Sólo admin y contador, en las completas cargadas a mano (las de adjudicaciones tienen el suyo). */
  atrasado?: AtrasadoDistribucion | null;
}

/** Lo atrasado de un cambio de participación (Mati, 2026-10-18): siempre retroactivo. */
export interface AtrasadoDistribucion {
  desde_anterior: string | null;
  base_usd: string; // gastos repartidos antes del cambio
  lineas: LineaAtrasado[]; // primero los que deben
}

export interface LineaAtrasado {
  fiduciante_id: number;
  nombre: string;
  diferencia: string; // % después − % antes
  monto_usd: string; // > 0 recibe (bajó), < 0 debe (subió)
}

export interface AccesoPortal {
  fiduciante: Fiduciante;
  password_temporal: PasswordTemporal | null;
}

export interface Rubro {
  id: number;
  nombre: string;
  activo: boolean; // false = archivado
  clave: string | null; // "fee" = rubro reservado del fee de administración: no se elige a mano
}

export type CondicionIva = "responsable_inscripto" | "monotributo" | "exento" | "no_alcanzado";

/** Etiqueta de la agenda de proveedores (global, como la agenda). */
export interface Etiqueta {
  id: number;
  nombre: string;
}

/** Proveedor de la agenda: ficha completa (sólo admin y contador la leen). */
export interface Proveedor {
  id: number;
  nombre: string;
  etiquetas: string[]; // nombres, ordenados
  activo: boolean;
  cuit: string | null;
  condicion_iva: CondicionIva | null;
  contacto: string | null;
  telefono: string | null;
  email: string | null;
  banco: string | null;
  cbu_alias: string | null;
  notas: string | null;
  recomendado_por: string | null; // de parte de quién viene (Mati, 2026-10-19)
  validado: boolean; // ya trabajamos con él y damos fe de que trabaja bien
}

/** Respuesta de DELETE de rubros y proveedores: si estaba en uso, se archiva en vez de borrarse. */
export interface ResultadoBaja {
  resultado: "borrado" | "archivado";
}

export type TipoGasto = "A" | "B";
export type EstadoGasto = "pendiente" | "parcial" | "pagado";
export type MedioPago = "transferencia" | "efectivo" | "cheque" | "otro";
/** Un gasto B también se paga por cuenta corriente: se les acredita a fiduciantes o grupos (los cobros no). */
export type MedioPagoGasto = MedioPago | "cuenta_corriente";
/** De dónde salió la plata de un gasto, según sus pagos (null: sin pagos todavía). */
export type FormaPago = "caja" | "cuenta_corriente" | "mixta";

/** A quién se le acredita un pago por cuenta corriente: un fiduciante o un grupo, con su %. */
export interface AsignacionPago {
  fiduciante_id: number | null;
  grupo_id: number | null;
  nombre: string;
  porcentaje: string;
}
export type { Moneda };

export interface Ref {
  id: number;
  nombre: string;
}

export interface Adjunto {
  id: number;
  entidad: "gasto" | "pago" | "aporte" | "unidad" | "unidad_plano" | "unidad_render" | "unidad_360" | "tipologia_render" | "maqueta" | "venta" | "cobro" | "cambio_moneda" | "cronograma" | "terreno" | "cotizacion" | "liquidacion_fee";
  entidad_id: number;
  nombre: string;
  tipo_mime: string;
  tamanio: number;
  creado_en: string;
}

export interface Pago {
  id: number;
  gasto_id: number;
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  medio: MedioPagoGasto;
  asignaciones: AsignacionPago[];
  nota: string | null;
  caja_id: number;
  adjuntos: Adjunto[];
  creado_en: string;
}

export interface ResumenFee {
  id: number;
  monto: string;
  pagado: string;
  saldo: string;
  cantidad_pagos: number;
  cantidad_adjuntos: number;
}

export interface Gasto {
  id: number;
  fecha: string;
  proveedor: Ref | null;
  rubro: Ref;
  tipo: TipoGasto;
  descripcion: string;
  comprobante_nro: string | null;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  lleva_fee: boolean;
  fee_porcentaje: string | null;
  es_fee: boolean;
  gasto_origen_id: number | null;
  fee: ResumenFee | null;
  pagado: string;
  saldo: string;
  estado: EstadoGasto;
  cantidad_pagos: number;
  cantidad_adjuntos: number;
  /** Tiene un adjunto propio (el comprobante); los de sus pagos son recibos. */
  tiene_comprobante: boolean;
  /** El primero de esos adjuntos: el ícono de la columna Comprobante lo descarga. */
  comprobante_id?: number | null;
  forma_pago: FormaPago | null;
  creado_en: string;
  /** La factura de un presupuesto aceptado ("Pérez SRL — Demolición"); `aceptado` false si después dejó de estarlo. */
  cotizacion: RefPresupuesto | null;
  /** El pago de su forma de pago que cubre (el concepto). */
  cuota: Ref | null;
  /** "Rubro › ítem". */
  item: Ref | null;
  /** La fila de la compra del terreno (id 0): el precio acordado con los pagos a cuenta como pagos. */
  compra_terreno?: boolean;
}

export interface RefPresupuesto {
  id: number;
  nombre: string;
  aceptado: boolean;
}

/** GET /gastos/vinculables: lo que ofrece el formulario de la factura (sólo admin). */
export interface Vinculables {
  presupuestos: PresupuestoVinculable[];
  items: ItemDeFactura[];
}

export interface PresupuestoVinculable {
  id: number;
  nombre: string;
  descripcion: string | null;
  moneda: Moneda | null;
  monto: string | null;
  monto_usd: string | null;
  /** El rubro de los ítems que cubre, si es uno solo: se propone al elegirlo. */
  rubro_id: number | null;
  /** Los ítems que cubre (van primero en el desplegable del ítem). */
  items: number[];
  cuotas: CuotaVinculable[];
}

export interface CuotaVinculable {
  id: number;
  concepto: string;
  porcentaje: string;
  monto_usd: string | null;
  facturado_usd: string;
  pagado_usd: string;
}

export interface ItemDeFactura {
  id: number;
  nombre: string;
  rubro_id: number;
}

/** POST /gastos/escanear: lo que propone Escanear factura (no guardó nada). */
export interface EscaneoFactura {
  proveedor_id: number | null;
  proveedor_nombre: string | null;
  proveedor_cuit: string | null;
  fecha: string | null;
  comprobante_nro: string | null;
  descripcion: string | null;
  monto: string | null;
  moneda: Moneda | null;
  cotizacion_id: number | null;
  cuota_id: number | null;
  rubro_id: number | null;
  /** "El archivo no parece una factura…" */
  aviso: string | null;
}

/** Una factura en la ficha de su presupuesto. */
export interface FacturaVinculada {
  id: number;
  fecha: string;
  descripcion: string;
  comprobante_nro: string | null;
  cuota_id: number | null;
  monto: string;
  moneda: Moneda;
  monto_usd: string;
  /** Al cambio de la factura. */
  pagado_usd: string;
  estado: EstadoGasto;
}

// ------------------------------------------------------------------ presupuestos recibidos (cotizaciones)

export type EstadoCotizacion = "pedido" | "recibido" | "aceptado" | "descartado";

/** Presupuesto que mandó un proveedor. Montos como string; `monto`, `moneda` y `monto_usd` son null mientras está "pedido". */
export interface Cotizacion {
  id: number;
  proveedor_id: number;
  proveedor: string;
  proveedor_validado: boolean;
  proveedor_recomendado_por: string | null;
  /** La subcategoría (un ítem del presupuesto de obra); null en los de antes que todavía no se ubicaron. */
  item_id: number | null;
  item: string | null;
  /** "1.1", como en Obra → Presupuesto. */
  numero: string | null;
  /** La categoría (el rubro de la subcategoría). */
  rubro_id: number | null;
  rubro: string | null;
  /** Sólo los de antes de las subcategorías. */
  etiqueta_id: number | null;
  etiqueta: string | null;
  descripcion: string | null;
  fecha: string;
  estado: EstadoCotizacion;
  monto: string | null;
  moneda: Moneda | null;
  tipo_cambio: string | null;
  monto_usd: string | null;
  valido_hasta: string | null;
  notas: string | null;
  /** Qué cubre; null = sin marcar. */
  composicion: Composicion | null;
  /** "60.00": sólo en mixto. */
  porcentaje_materiales: string | null;
  cantidad_adjuntos: number;
  creado_en: string;
}

/** Qué cubre un presupuesto recibido: con sus pagos partidos por el % de materiales (mixto) para el cash flow. */
export type Composicion = "materiales" | "mano_obra" | "mixto";

export interface ComposicionPropuesta {
  composicion: Composicion;
  porcentaje_materiales: string | null;
}

/** POST /cotizaciones/escanear: lo que se leyó del presupuesto escaneado, para revisar en el form. No se guardó nada. */
export interface DuplicadoEscaneo {
  id: number;
  proveedor: string;
  fecha: string;
  monto: string | null;
  moneda: Moneda | null;
  estado: EstadoCotizacion;
  mismo_archivo: boolean;
}

export interface EscaneoCotizacion {
  /** De la agenda (por CUIT o nombre); null → elegirlo o agregarlo con `proveedor_nombre`. */
  proveedor_id: number | null;
  proveedor_nombre: string | null;
  proveedor_cuit: string | null;
  /** La subcategoría que eligió la lectura; null si no se entendió. */
  item_id: number | null;
  descripcion: string | null;
  fecha: string | null;
  monto: string | null;
  moneda: Moneda | null;
  valido_hasta: string | null;
  notas: string | null;
  /** El desglose leído, sin vincular a ítems del rubro. */
  lineas: LineaLeida[];
  /** La forma de pago leída (los % del total). */
  cuotas: CuotaLeida[];
  composicion: Composicion | null;
  /** Mixto sin % entendible: null (al guardar se toma el de los ítems). */
  porcentaje_materiales: string | null;
  /** P. ej. "El archivo no parece un presupuesto…". */
  aviso: string | null;
  /** Un presupuesto ya cargado que parece el mismo: con `mismo_archivo` no se leyó nada (el archivo ya es su adjunto). */
  duplicado: DuplicadoEscaneo | null;
}

export interface LineaLeida {
  descripcion: string;
  unidad: string | null;
  cantidad: string | null;
  precio_unitario: string | null;
}

export interface CuotaLeida {
  concepto: string;
  /** "70.00": % del total. */
  porcentaje: string;
  fecha: string | null;
}

/** Cuándo cae un pago: fecha fija, sin fecha, o según el cronograma (docs/modulo-cash-flow.md § Cuándo cae cada pago). */
export type AnclaCuota = "fecha" | "sin_fecha" | "inicio_rubro" | "fin_rubro" | "durante_rubro" | "inicio_item" | "fin_item";

/** Un pago de la forma de pago de un presupuesto recibido. `monto` = total × % (el último absorbe el redondeo); null sin total. */
export interface CuotaCotizacion {
  id: number;
  orden: number;
  concepto: string;
  porcentaje: string;
  /** La fija (ancla `fecha`). */
  fecha: string | null;
  ancla: AnclaCuota;
  rubro_id: number | null;
  rubro: string | null;
  item_id: number | null;
  item: string | null;
  /** Corridos, ± desde el inicio o el fin del rubro o del ítem. */
  dias: number;
  /** Según el cronograma (sólo para el admin): el día, o del primero al último del rubro si se reparte. */
  prevista: string | null;
  prevista_hasta: string | null;
  /** Sin fecha, por qué ("Falta la fecha de inicio de obra"). */
  motivo: string | null;
  monto: string | null;
  monto_usd: string | null;
  /** Lo de sus facturas, en USD al cambio de cada una. */
  facturado_usd: string;
  pagado_usd: string;
}

/** Un renglón del desglose de un presupuesto recibido, en su moneda. `subtotal` null sin cantidad o sin precio. */
export interface LineaCotizacion {
  id: number;
  orden: number;
  descripcion: string;
  unidad: string | null;
  cantidad: string | null;
  precio_unitario: string | null;
  subtotal: string | null;
  /** Ítem del presupuesto de un rubro con la etiqueta del presupuesto recibido, o null. */
  presupuesto_item_id: number | null;
}

/** Un ítem al que se puede vincular una línea: de un rubro con la etiqueta del presupuesto recibido. */
export interface ItemVinculable {
  id: number;
  rubro_id: number;
  rubro: string;
  descripcion: string;
  unidad: string | null;
}

/** GET /presupuesto/categorias: el árbol de Plata → Presupuestos (rubros visibles y sus ítems, numerados). */
export interface Subcategoria {
  id: number;
  numero: string;
  descripcion: string;
}

export interface Categoria {
  id: number;
  numero: string;
  nombre: string;
  activo: boolean;
  /** "previos" = Trámites previos: primera, se numera 0. */
  clave: string | null;
  subcategorias: Subcategoria[];
}

/** Un paso del manual de obra en una subcategoría (GET /manual/pasos; docs/modulo-manual.md). */
export interface PasoManual {
  id: number;
  item_id: number;
  orden: number;
  texto: string;
  quien: string | null;
  detalle: string | null;
  pide_dato: boolean;
  respuesta: string | null;
  hecho_en: string | null; // ISO; null = pendiente
  /** Lo que hace falta para ejecutarlo (Mati, 2026-10-30). */
  datos: DatoPaso[];
}

/** Un dato que necesita un paso: lo anotado a mano (`valor`) o, si es una variable del proyecto, lo que sabe el sistema. */
export interface DatoPaso {
  nombre: string;
  quien: string | null;
  variable: string | null;
  valor: string | null;
  valor_sistema: string | null;
}

/** GET /cotizaciones/escanear: hay clave de la API de Claude cargada en el servidor. */
export interface EscaneoDisponible {
  disponible: boolean;
}

/** GET /cotizaciones/{id}: la fila más sus adjuntos (el PDF del presupuesto). */
export interface CotizacionDetalle extends Cotizacion {
  adjuntos: Adjunto[];
  lineas: LineaCotizacion[];
  /** En la moneda del presupuesto; null si ninguna línea tiene subtotal. */
  suma_lineas: string | null;
  /** monto − suma_lineas (IVA, descuentos…); null si falta alguno. */
  diferencia: string | null;
  items_vinculables: ItemVinculable[];
  /** La forma de pago, en orden. */
  cuotas: CuotaCotizacion[];
  /** "100.00"; la ficha avisa si hay pagos y no da 100. */
  suma_porcentajes: string;
  /** Según los ítems vinculados a sus líneas (o los de los rubros con su etiqueta); null si no tienen precios. */
  composicion_propuesta: ComposicionPropuesta | null;
  /** Sus facturas (Facturas con este presupuesto), por fecha. */
  facturas: FacturaVinculada[];
  facturado_usd: string;
  pagado_usd: string;
  /** Total − pagado (negativo: se pagó de más); null sin total. */
  falta_pagar_usd: string | null;
}

// ------------------------------------------------------------------ terreno

/** Un pago a cuenta (seña, refuerzo) o un gasto de la compra del terreno: por dentro, un gasto pagado del rubro "Terreno". */
export interface ItemCompra {
  gasto_id: number;
  fecha: string;
  concepto: string;
  proveedor: Ref | null; // sólo en un gasto del rubro cargado desde Gastos
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  lleva_fee: boolean;
  fee_monto_usd: string | null; // honorarios del fiduciario que generó, en USD
  pagado_por: string | null; // pago a cuenta que pagó un fiduciante de su bolsillo (su aporte); null = el fideicomiso
  caja_id: number | null; // la del pago (la primera, si salió de varias); null para el fiduciante (no ve las cajas)
  cajas: { caja_id: number; monto: string }[]; // de qué cajas salió y cuánto de cada una
  adjuntos: Adjunto[];
}

/** Un gasto de la compra que todavía no se pagó: no mueve plata; al confirmar la compra pasa a gasto pagado el día de
 * la escritura. */
export interface ItemPrevisto {
  id: number;
  concepto: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  lleva_fee: boolean;
  fee_monto_usd: string | null; // los honorarios que va a generar, con el fee de hoy
  adjuntos: Adjunto[];
}

/** En USD. Los pagos a cuenta son parte del acordado: bajan el saldo de la escritura, no suman al costo. */
export interface CompraTerreno {
  precio_acordado_usd: string | null;
  pagos: ItemCompra[];
  gastos: ItemCompra[];
  previstos: ItemPrevisto[];
  pagado_usd: string;
  saldo_escritura_usd: string | null;
  gastos_usd: string;
  previstos_usd: string;
  total_usd: string | null; // acordado + gastos + previstos
  honorarios_usd: string;
  confirmada: boolean;
  confirmada_en: string | null;
  escritura_adjunto_id: number | null;
  /** Lo que falta para confirmar la compra (los mismos textos del 422); vacía si se puede confirmar. */
  faltan_para_confirmar: string[];
}

export interface Terreno {
  direccion: string | null; // dirección formateada (la usa el mapa)
  calle: string | null;
  altura: string | null;
  entre_calle_1: string | null;
  entre_calle_2: string | null;
  localidad: string | null;
  partido: string | null;
  provincia: string | null;
  codigo_postal: string | null;
  latitud: string | null; // decimal como texto
  longitud: string | null;
  google_place_id: string | null;
  /** Hacia dónde mira el frente, en grados desde el norte (0 N, 90 E…): el sol del recorrido inmersivo. */
  frente_azimut: number | null;
  /** El backend tiene clave de Google: la UI ofrece el buscador de direcciones. */
  autocompletado: boolean;
  superficie_m2: string | null;
  zonificacion: string | null;
  fecha_escritura: string | null;
  compra: CompraTerreno | null; // null para el escribano (no ve la plata)
  adjuntos: Adjunto[];
}

/** Sugerencia del autocompletado de direcciones (Google Places, por el backend). */
export interface SugerenciaDireccion {
  place_id: string;
  texto: string;
  secundario: string | null;
}

/** Detalle de una dirección elegida entre las sugerencias. */
export interface DetalleDireccion {
  direccion: string | null;
  calle: string | null;
  altura: string | null;
  localidad: string | null;
  partido: string | null;
  provincia: string | null;
  codigo_postal: string | null;
  latitud: string | null;
  longitud: string | null;
  google_place_id: string | null;
}

export interface GastoDetalle extends Gasto {
  pagos: Pago[];
  adjuntos: Adjunto[];
  /** A quién se le acreditó el último pago por cuenta corriente a este proveedor: se propone en el próximo. */
  cuenta_corriente_sugerida: AsignacionPago[];
  /** Es un pago a cuenta del terreno: en Gastos va dentro de la compra del terreno. */
  pago_a_cuenta_terreno?: boolean;
}

/** Un pago a cuenta del terreno visto como pago de la compra (`GET /gastos/compra-terreno`). */
export interface PagoCompraTerreno {
  gasto_id: number;
  fecha: string;
  concepto: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  pagado_por: string | null;
  adjuntos: Adjunto[];
}

export interface CompraTerrenoDetalle {
  gasto: Gasto;
  pagos: PagoCompraTerreno[];
  fecha_escritura: string | null;
  confirmada: boolean;
}

export interface TotalesMoneda {
  monto: string;
  pagado: string;
  saldo: string;
}

export interface TotalesParte {
  monto_usd: string;
  pagado_usd: string;
}

export interface TotalesGastos {
  ARS: TotalesMoneda;
  USD: TotalesMoneda;
  monto_usd: string;
  pagado_usd: string;
  saldo_usd: string;
  /** La fila de la compra del terreno, si está en el listado; `obra` es todo lo demás (Mati, 2026-11-03). */
  terreno: TotalesParte | null;
  obra: TotalesParte;
}

export interface ListadoGastos {
  items: Gasto[];
  totales: TotalesGastos;
}

export interface TipoCambioSugerido {
  tipo_cambio: string | null;
}

// ------------------------------------------------------------------ llamados de capital y aportes

export type EstadoLlamado = "abierto" | "cubierto" | "vencido";

export interface MiLinea {
  pedido_usd: string;
  imputado_usd: string;
  pendiente_usd: string;
  vencida: boolean;
}

export interface LineaLlamado extends MiLinea {
  fiduciante_id: number;
  nombre: string;
  clase: ClaseFiduciante;
  porcentaje: string;
}

// ------------------------------------------------------------------ historial

export interface CambioHistorial {
  campo: string;
  antes: string | null;
  despues: string | null;
}

export interface MovimientoHistorial {
  id: number;
  creado_en: string;
  usuario: string;
  accion: string; // crear | editar | borrar | descargar | …
  entidad: string;
  entidad_id: string;
  titulo: string;
  cambios: CambioHistorial[];
  deshace_id: number | null;
  deshecho_por: { id: number; usuario: string; creado_en: string } | null;
  se_puede_deshacer: boolean;
  por_que_no: string | null;
}

export interface Historial {
  items: MovimientoHistorial[];
  siguiente: number | null;
}

export interface Llamado {
  id: number;
  numero: number;
  fecha: string;
  vencimiento: string;
  descripcion: string | null;
  monto_usd: string;
  cubierto_con_ventas_usd: string;
  montos_a_mano: boolean; // emitido desde Pedido de capital con montos redondos: no se reparte de nuevo al editar
  aportado_usd: string;
  pendiente_usd: string;
  estado: EstadoLlamado;
  creado_en: string;
}

export interface LlamadoDetalle extends Llamado {
  distribucion_id: number;
  lineas: LineaLlamado[]; // al fiduciante le llega sólo la suya
}

export interface TotalesLlamados {
  pedido_usd: string;
  aportado_usd: string;
  pendiente_usd: string;
  vencido_usd: string;
}

export interface ListadoLlamados {
  items: Llamado[];
  totales: TotalesLlamados;
}

export interface VistaPreviaLlamado {
  distribucion_id: number;
  vigente_desde: string;
  parte_b_usd: string;
  parte_a_usd: string;
  disponible_ventas_usd: string; // puede ser negativo
  sugerido_usd: string;
  cubierto_con_ventas_usd: string; // el pedido o, si no se pidió, la sugerencia
  lineas: { fiduciante_id: number; nombre: string; clase: ClaseFiduciante; porcentaje: string; pedido_usd: string }[];
}

/** GET /llamados/gastos-a-cubrir: base de la propuesta del monto de un llamado. */
export interface ItemACubrir {
  tipo: "gasto" | "terreno";
  gasto_id: number | null; // null en lo de la compra del terreno sin confirmar
  fecha: string | null; // null: compra del terreno sin fecha de escritura
  concepto: string;
  proveedor: string | null;
  saldo_usd: string;
}

export interface GastosACubrir {
  hasta: string;
  total_usd: string;
  items: ItemACubrir[];
}

export interface Aporte {
  id: number;
  fiduciante_id: number;
  nombre: string;
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  nota: string | null;
  /** null para el fiduciante (ve sólo el total de la caja). */
  caja_id: number | null;
  /** Parte del aporte de un grupo: se corrige desde Grupos. */
  aporte_grupo_id?: number | null;
  grupo?: string | null;
  adjuntos: Adjunto[];
  creado_en: string;
}

// ---- Grupos de aportantes (Mati, 2026-10-19)

export interface MiembroGrupo {
  fiduciante_id: number;
  nombre: string;
  porcentaje: string;
  con_el_grupo_usd: string;
  por_su_cuenta_usd: string;
  saldo_usd: string;
}

export interface Grupo {
  id: number;
  nombre: string;
  tiene_aportes: boolean;
  /** Por % descendente. */
  miembros: MiembroGrupo[];
  totales: { con_el_grupo_usd: string; por_su_cuenta_usd: string; saldo_usd: string };
}

export interface ParteAporteGrupo {
  aporte_id: number;
  fiduciante_id: number;
  nombre: string;
  porcentaje: string;
  monto: string;
  monto_usd: string;
}

export interface AporteGrupo {
  id: number;
  grupo_id: number;
  grupo: string;
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  nota: string | null;
  caja_id: number | null;
  partes: ParteAporteGrupo[];
  /** No se edita ni se borra desde Grupos (ej. es un pago a cuenta del terreno): el porqué. */
  bloqueo?: string | null;
}

export interface Cuenta {
  fiduciante_id: number;
  nombre: string;
  pedido_usd: string;
  aportado_usd: string;
  pendiente_usd: string;
  a_favor_usd: string; // aportado de más sobre los llamados
  vencido_usd: string; // de los llamados
  gastos_usd: string; // su parte de los gastos efectuados
  ajustes_usd: string; // lo atrasado de las adjudicaciones a los clase B: > 0 a favor, < 0 debe
  saldo_usd: string; // aportado − gastos + ajustes: > 0 a favor, < 0 debe
}

export interface TotalesCuentas {
  pedido_usd: string;
  aportado_usd: string;
  pendiente_usd: string;
  a_favor_usd: string;
  vencido_usd: string;
  gastos_usd: string;
  ajustes_usd: string; // siempre 0: lo que paga uno lo cobra otro
  saldo_usd: string;
  sin_repartir_usd: string; // gastos sin distribución vigente a su fecha: no se le cargan a nadie
}

export interface ListadoCuentas {
  items: Cuenta[];
  totales: TotalesCuentas;
}

export interface LlamadoMio extends MiLinea {
  id: number;
  clase: ClaseFiduciante;
  numero: number;
  fecha: string;
  vencimiento: string;
}

export type TipoMovimiento = "aporte" | "gasto" | "terreno" | "adjudicacion" | "participacion";

export interface Movimiento {
  fecha: string;
  tipo: TipoMovimiento;
  concepto: string;
  aporte_id: number | null;
  gasto_id: number | null;
  adjudicacion_id: number | null;
  total_usd: string | null; // el gasto entero
  porcentaje: string | null; // su % en ese gasto
  debe_usd: string;
  haber_usd: string;
  saldo_usd: string; // acumulado
}

/** La cuenta corriente de un fiduciante (`/cuentas/{id}` y `/cuentas/mia`). */
export interface CuentaDetalle extends Cuenta {
  llamados: LlamadoMio[]; // más nuevo primero; una fila por clase
  aportes: Aporte[];
  movimientos: Movimiento[]; // de lo más viejo a lo más nuevo, con el saldo acumulado
}

export type CuentaMia = CuentaDetalle;

// ------------------------------------------------------------------ adjudicaciones a los clase B

export interface UnidadAdjudicada {
  id: number;
  nombre: string; // "1° A", "cochera SS1 1"
  porcentaje: string; // % del fideicomiso, congelado al adjudicar
}

export interface ItemVigente {
  fiduciante_id: number;
  nombre: string;
  clase: ClaseFiduciante;
  porcentaje: string;
}

export interface VistaPreviaAdjudicacion {
  porcentaje_anterior: string;
  porcentaje_nuevo: string;
  diferencia: string; // > 0 le falta (alguien cede), < 0 le sobra (alguien recibe)
  unidades: UnidadAdjudicada[];
  base_usd: string; // gastos repartidos antes de la fecha
  atrasado_usd: string;
  items: ItemVigente[]; // la distribución vigente a esa fecha
}

export interface ContraparteAdjudicacion extends ItemVigente {
  monto_usd: string; // su parte de lo atrasado
}

export interface Adjudicacion {
  id: number;
  fecha: string;
  fiduciante_id: number;
  nombre: string;
  unidades: UnidadAdjudicada[];
  porcentaje_anterior: string;
  porcentaje_nuevo: string;
  diferencia: string;
  contrapartes: ContraparteAdjudicacion[];
  atrasado_usd: string;
  nota: string | null;
  deshacer: string | null; // null = se puede deshacer; si no, por qué no
}

// ------------------------------------------------------------------ unidades

export type TipoUnidad = "departamento" | "cochera" | "baulera" | "local" | "oficina";

/** GET /unidades/proyecto: ficha y estado, sin precio ni comprador (lo que ve el fiduciante en Proyecto). */
export type UnidadProyecto = Pick<
  Unidad,
  "id" | "numero" | "piso" | "tipo" | "sup_cubierta_m2" | "sup_semicubierta_m2" | "sup_descubierta_m2" | "sup_total_m2" | "ambientes" | "orientacion" | "estado_venta"
> & {
  /** Imágenes del plano y renders: los ven los cuatro roles. */
  planos: Adjunto[];
  renders: Adjunto[];
  /** Al frente (true) o al contrafrente, como en la maqueta; null en cocheras y bauleras. */
  frente: boolean | null;
  /** Hacia dónde mira su fachada (0 N, 90 E…), con el rumbo del terreno; null si no está cargado. */
  mira_a: number | null;
};

/** GET /maqueta: la maqueta 3D del edificio (un .glb), o null si todavía no se subió. */
export interface Maqueta {
  adjunto: Adjunto | null;
}

/** Un rectángulo de la planta, en metros: x a lo ancho, y de la fachada (0) al pasillo; el balcón queda en y < 0. */
export interface RectPlanta {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** GET /unidades/{id}/recorrido: la unidad por dentro, en cajas, para el recorrido inmersivo (Mati, 2026-11-01). */
export interface RecorridoUnidad {
  unidad: { id: number; tipo: TipoUnidad; piso: number; numero: string };
  /** La fachada de la unidad es la del frente (si no, la del contrafrente). */
  frente: boolean;
  ancho_m: number;
  fondo_m: number;
  /** Altura de la losa sobre la vereda. */
  nivel_m: number;
  alto_piso_m: number;
  alto_muro_m: number;
  /** Lo cubierto y el balcón. */
  pisos: RectPlanta[];
  ambientes: (RectPlanta & { tipo: string; nombre: string })[];
  /** Muros, vidrios, puertas, barandas y muebles; z en metros sobre la losa. */
  cajas: (RectPlanta & { material: string; z0: number; z1: number })[];
  sitio: { latitud: string | null; longitud: string | null; frente_azimut: number | null };
  /** Fotos 360° fotorrealistas (equirectangulares, de Blender), una por ambiente: el nombre del archivo dice cuál. */
  fotos_360: Adjunto[];
}

/** POST /maqueta/ejemplo: la maqueta inventada con las unidades, cuántos planos y vistas desde arriba subió y la altura de piso a piso. */
export interface MaquetaEjemplo {
  adjunto: Adjunto;
  planos: number;
  vistas: number;
  alto_piso_m: string;
}

export interface Unidad {
  id: number;
  numero: string;
  piso: number; // 0 = PB, negativos = subsuelos
  tipo: TipoUnidad;
  sup_cubierta_m2: string;
  sup_semicubierta_m2: string;
  sup_descubierta_m2: string;
  sup_total_m2: string;
  ambientes: number | null;
  orientacion: string | null;
  precio_lista_usd: string | null;
  fuera_de_venta: boolean;
  motivo_fuera_de_venta: string | null;
  notas: string | null;
  adjuntos_cantidad: number;
  creado_en: string;
  estado_venta: EstadoUnidad;
  venta: { id: number; numero: number; estado: EstadoVenta } | null;
  adjudicada_a: { adjudicacion_id: number; fiduciante_id: number; nombre: string } | null; // el clase B al que se adjudicó
  coef_cubierta: string | null; // multiplicadores propios del depto (null = los por defecto)
  coef_semicubierta: string | null;
  coef_descubierta: string | null;
  ajuste: string;
  m2_ponderados: string; // deptos: Σ superficie × multiplicador; el resto, sus m² tal cual
  coef_altura: string | null; // del piso; null si no es depto
  m2_homologados: string; // deptos: ponderados × coef. altura; el resto, sus m² ponderados
  porcentaje_fideicomiso: string | null; // % sobre el total de las no fuera de venta (cierra en 100); null si está fuera de venta
  precio_sugerido_usd: string | null; // m² homologados × precio base, a USD 1.000; null sin precio base o fuera de venta
  tipologia_id: number | null; // deptos iguales comparten los renders de su tipología
}

export interface UnidadDetalle extends Unidad {
  /** Planos y archivos internos (no los ve el fiduciante). */
  adjuntos: Adjunto[];
  /** Lo que se muestra en Proyecto (los renders, sólo los propios: los de la tipología vienen en `Tipologia`). */
  planos: Adjunto[];
  renders: Adjunto[];
}

/** Deptos iguales que comparten los renders: en Proyecto se suman a los de cada uno (`GET /tipologias`). */
export interface Tipologia {
  id: number;
  nombre: string;
  unidades: Pick<Unidad, "id" | "tipo" | "piso" | "numero">[]; // por piso y número
  renders: Adjunto[];
}

export interface ResumenTipoUnidad {
  cantidad: number;
  disponibles: number;
  reservadas: number;
  vendidas: number;
  adjudicadas: number;
  sup_total_m2: string;
  precio_lista_usd: string;
}

export interface ResumenUnidades {
  total: ResumenTipoUnidad;
  por_tipo: Record<TipoUnidad, ResumenTipoUnidad>;
  m2_en_venta: string; // m² homologados de las no fuera de venta: el 100 % del fideicomiso
  m2_reservados_clase_b: string; // m² en venta × % de los B de la distribución vigente
  m2_disponibles_para_vender: string; // m2_en_venta − m2_reservados_clase_b
}

export interface DepartamentoPonderacion {
  id: number;
  numero: string;
  piso: number;
  sup_cubierta_m2: string;
  sup_semicubierta_m2: string;
  sup_descubierta_m2: string;
  coef_cubierta: string; // los que usa: los propios o, si no tiene, los por defecto
  coef_semicubierta: string;
  coef_descubierta: string;
  propios: boolean;
  coef_altura: string; // el que usa: el propio o, si no tiene, el estándar de su piso
  coef_altura_estandar: string; // el estándar de su piso (Configuración → Ponderación)
  coef_altura_propio: boolean;
  fuera_de_venta: boolean;
}

export interface OtraUnidadPonderacion {
  id: number;
  tipo: TipoUnidad;
  piso: number;
  numero: string;
  m2: string; // superficie total (× multiplicador = sus m² ponderados y homologados)
  multiplicador: string; // por defecto "1.000"
  fuera_de_venta: boolean;
}

export interface Ponderacion {
  coef_cubierta: string; // multiplicadores por defecto del desarrollo (Configuración → Ponderación)
  coef_semicubierta: string;
  coef_descubierta: string;
  departamentos: DepartamentoPonderacion[]; // por piso y número
  otras_unidades: OtraUnidadPonderacion[]; // cocheras y demás (también las fuera de venta, que no entran al %)
  precio_base_usd_m2: string | null; // USD por m² homologado (null = sin precio sugerido)
  coef_altura_base: string; // coef. altura estándar del 1° piso (por defecto "0.920")…
  coef_altura_paso: string; // …y cuánto sube por piso (por defecto "0.030")
  ponderados_guardados: boolean; // el paso 1 (m² ponderados) ya se guardó: la pestaña lo muestra plegado
  homologados_guardados: boolean; // ídem el paso 2 (calidad y altura → m² homologados)
}

// ------------------------------------------------------------------ CAC, compradores, ventas y cobros

export interface IndiceCac {
  periodo: string; // "2026-09"
  valor: string;
}

export type TipoComprador = "humana" | "juridica";

/** Referencia al comprador: id y nombre. */
export interface CompradorPublico {
  id: number;
  nombre: string;
}

export interface Comprador extends CompradorPublico {
  tipo: TipoComprador;
  documento: string | null;
  email: string | null;
  telefono: string | null;
  domicilio: string | null;
  notas: string | null;
  creado_en: string;
}

export interface CompradorListado extends CompradorPublico {
  ventas_cantidad: number;
  tipo: TipoComprador;
  documento: string | null;
  email: string | null;
  telefono: string | null;
}

export type EstadoVenta = "reserva" | "boleto" | "escriturada" | "desistida";
export type Modalidad = "USD" | "ARS_CAC";
export type TipoCuota = "anticipo" | "cuota" | "refuerzo" | "posesion";
export type EstadoCuota = "pendiente" | "parcial" | "cobrada" | "vencida";
export type EstadoUnidad = "disponible" | "reservada" | "vendida" | "fuera_de_venta" | "adjudicada";

export interface UnidadRef {
  id: number;
  tipo: TipoUnidad;
  numero: string;
  piso: number;
}

export interface VentaResumen {
  id: number;
  numero: number;
  estado: EstadoVenta;
  unidades: UnidadRef[];
}

export interface CompradorDetalle {
  comprador: Comprador;
  ventas: VentaResumen[];
}

export interface CacRef {
  periodo: string;
  valor: string;
}

export interface Cobro {
  id: number;
  cuota_id: number;
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  monto_en_cuenta: string;
  medio: MedioPago;
  nota: string | null;
  caja_id: number;
  adjuntos: Adjunto[];
  creado_en: string;
}

export interface Devolucion {
  id: number;
  venta_id: number;
  fecha: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  monto_en_cuenta: string;
  medio: MedioPago;
  nota: string | null;
  caja_id: number;
  creado_en: string;
}

export interface Cuota {
  id: number;
  numero: number;
  tipo: TipoCuota;
  vencimiento: string;
  monto_base: string;
  monto_actualizado: string | null;
  provisorio: boolean;
  sin_indice: boolean;
  cac_aplicado: CacRef | null;
  cobrado: string;
  saldo: string | null;
  estado: EstadoCuota;
  cobros: Cobro[];
}

export interface ProximaCuota {
  numero: number;
  vencimiento: string;
  monto_actualizado: string | null;
  provisorio: boolean;
}

export interface Venta {
  id: number;
  numero: number;
  fecha: string;
  estado: EstadoVenta;
  modalidad: Modalidad;
  moneda_cuenta: Moneda;
  comprador: Comprador;
  unidades: UnidadRef[];
  precio: string;
  cobrado: string;
  saldo: string;
  saldo_incompleto: boolean;
  vencido: string;
  cobrado_usd: string;
  proxima_cuota: ProximaCuota | null;
  creado_en: string;
}

export interface VentaDetalle extends Venta {
  cac_origen: CacRef | null;
  boleto_fecha: string | null;
  escritura_fecha: string | null;
  desistida_fecha: string | null;
  desistida_motivo: string | null;
  notas: string | null;
  cuotas: Cuota[];
  devoluciones: Devolucion[];
  devuelto: string;
  retenido: string;
  adjuntos: Adjunto[];
}

export interface TotalesVentas {
  USD: { precio: string; cobrado: string; saldo: string; vencido: string };
  ARS_CAC: { precio_base: string; cobrado: string; saldo: string; vencido: string };
  cobrado_usd: string;
}

export interface ListadoVentas {
  items: Venta[];
  totales: TotalesVentas;
}

export interface ResumenVentas {
  activas: number;
  reservas: number;
  boletos: number;
  escrituradas: number;
  desistidas: number;
  cobrado_usd: string;
  cuotas_vencidas: number;
}

export interface CuotaPrevia {
  numero: number;
  tipo: TipoCuota;
  vencimiento: string;
  monto_base: string;
}

export interface VistaPreviaPlan {
  cuotas: CuotaPrevia[];
  suma: string;
  cierra: boolean;
}

export interface CobroListado {
  id: number;
  fecha: string;
  venta: { id: number; numero: number };
  comprador: CompradorPublico;
  cuota: { id: number; numero: number; tipo: TipoCuota };
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  monto_usd: string;
  medio: MedioPago;
  nota: string | null;
  adjuntos: Adjunto[];
}

export interface ListadoCobros {
  items: CobroListado[];
  totales: { ARS: string; USD: string; monto_usd: string };
}

// ---- Caja, presupuesto y reportes

export type SentidoCambio = "compra_usd" | "venta_usd";
export type TipoLinea = "aporte" | "cobro" | "pago" | "devolucion" | "cambio" | "ganancia" | "transferencia";

export interface CambioMoneda {
  id: number;
  fecha: string;
  sentido: SentidoCambio;
  monto_usd: string;
  monto_ars: string;
  tipo_cambio: string;
  nota: string | null;
  caja_id: number;
  adjuntos: Adjunto[];
  creado_en: string;
}

/** Dónde está la plata (Mati, 2026-10-19): cada caja tiene pesos y dólares; una es la principal. */
export interface Caja {
  id: number;
  nombre: string;
  principal: boolean;
  activo: boolean;
}

export interface SaldoCaja extends Caja {
  saldo_ars: string;
  saldo_usd: string;
}

export interface Transferencia {
  id: number;
  fecha: string;
  origen_id: number;
  destino_id: number;
  moneda: Moneda;
  monto: string;
  nota: string | null;
  creado_en: string;
}

export interface ReferenciaLinea {
  tipo: "gasto" | "venta" | "fiduciante" | "cambio" | "transferencia" | "honorarios";
  id: number;
}

export interface LineaCaja {
  clave: string;
  fecha: string;
  tipo: TipoLinea;
  concepto: string;
  referencia: ReferenciaLinea | null;
  moneda: Moneda;
  ingreso: string;
  egreso: string;
  tipo_cambio: string | null;
  /** null en una transferencia en pesos (no tiene equivalente en USD). */
  monto_usd: string | null;
  saldo: string;
  /** null para el fiduciante (ve sólo el total). */
  caja_id: number | null;
  caja: string | null;
}

export interface AcreditadoMoneda {
  ARS: string;
  USD: string;
  total_usd: string;
}

export interface LibroCaja {
  items: LineaCaja[];
  totales: { ARS: { ingresos: string; egresos: string }; USD: { ingresos: string; egresos: string }; ingresos_usd: string; egresos_usd: string };
  saldo_inicial: { ARS: string; USD: string };
  /** Aportes del período por cuenta corriente y por fee B: no movieron plata, no están en el libro ni en los saldos. */
  acreditado_sin_caja: { cuenta_corriente: AcreditadoMoneda; fee_b: AcreditadoMoneda };
}

export interface SaldosCaja {
  saldo_ars: string;
  saldo_usd: string;
  total_usd: string | null;
  tipo_cambio_referencia: string | null;
  negativos: Moneda[];
  /** null para el fiduciante (ve sólo el total). */
  por_caja: SaldoCaja[];
}

export type EstadoPresupuesto = "sin_presupuesto" | "ok" | "alerta" | "excedido";

export interface RubroRef {
  id: number;
  nombre: string;
  activo: boolean;
  clave: string | null;
}

/** Resumen de un rubro: `presupuesto` es la suma de sus ítems con cantidad y precio (null si ninguno los tiene). */
export interface ItemPresupuesto {
  rubro: RubroRef;
  presupuesto: string | null;
  facturado: string;
  pagado: string;
  disponible: string | null;
  consumido_pct: string | null;
  estado: EstadoPresupuesto;
  cantidad_items: number;
  /** Etiquetas de la agenda que cubren el rubro: por ellas se vinculan los presupuestos recibidos. */
  etiquetas: string[];
  /** Suma de los presupuestos recibidos aceptados con alguna etiqueta del rubro. */
  aceptado_usd: string | null;
}

/** Un renglón del cómputo de un rubro. `cantidad` con 3 decimales; `subtotal` null sin cantidad o sin precio. */
export interface ItemComputo {
  id: number;
  rubro_id: number;
  orden: number;
  descripcion: string;
  unidad: string | null;
  cantidad: string | null;
  /** De los precios: ARS (USD sólo lo cargado antes del presupuesto en pesos, hasta que se carga el tipo de cambio). */
  moneda: Moneda;
  /** Precio unitario partido, en `moneda`; cualquiera de los dos puede faltar. */
  precio_materiales: string | null;
  precio_mano_obra: string | null;
  /** Materiales + mano de obra; null sin ninguno. */
  precio_unitario: string | null;
  /** cantidad × precio unitario, en `moneda`. */
  subtotal_moneda: string | null;
  /** En USD, con el tipo de cambio del presupuesto (null en pesos sin tipo de cambio). */
  subtotal_materiales: string | null;
  subtotal_mano_obra: string | null;
  /** La suma de las dos partes. */
  subtotal: string | null;
}

export interface RubroPresupuesto {
  resumen: ItemPresupuesto;
  tipo_cambio: string | null;
  /** Suma de los subtotales de cada parte; null si ningún ítem la tiene. */
  materiales: string | null;
  mano_obra: string | null;
  items: ItemFicha[];
  cotizaciones: Cotizacion[];
  comparacion_items: ComparacionItems;
}

/** Un ítem en la ficha del rubro, con lo facturado (facturas con ese ítem; null sin ninguna). */
/** Un presupuesto aceptado con líneas vinculadas al ítem: lo que cotizó para él, en USD. */
export interface AceptadoDeItem {
  cotizacion_id: number;
  proveedor: string;
  usd: string;
}

/** El ítem en la ficha del rubro, con su relación (todo en USD; null = no hay). */
export interface ItemFicha extends ItemComputo {
  aceptado_usd: string | null;
  aceptados: AceptadoDeItem[];
  facturado_usd: string | null;
  pagado_usd: string | null;
  facturas: FacturaVinculada[];
}

/** Cronograma por ítems (`GET /cronograma`, sólo admin): días hábiles relativos al inicio de obra (fin exclusivo). */
export type TipoVinculo = "FC" | "CC";

export interface PredecesoraCronograma {
  predecesora_id: number;
  tipo: TipoVinculo;
  espera: number;
}

export interface TareaCronograma {
  id: number;
  rubro_id: number;
  orden: number;
  descripcion: string;
  con_precio: boolean;
  duracion: number | null;
  no_antes_de: string | null;
  predecesoras: PredecesoraCronograma[];
  comienzo: number | null;
  fin: number | null;
  desde: string | null;
  hasta: string | null;
  holgura: number | null;
  critico: boolean;
}

export interface RubroCronograma {
  id: number;
  nombre: string;
  comienzo: number | null;
  fin: number | null;
  desde: string | null;
  hasta: string | null;
  critico: boolean;
  tareas: TareaCronograma[];
}

export interface Cronograma {
  inicio: string | null;
  fin: string | null;
  duracion: number;
  sin_programar: number;
  rubros: RubroCronograma[];
}

/** Ítem por ítem contra los presupuestos recibidos con líneas vinculadas al rubro (el aceptado primero). Todo en USD. */
export interface ComparacionItems {
  columnas: { cotizacion_id: number; proveedor: string; estado: EstadoCotizacion; moneda: Moneda }[];
  filas: { item_id: number; presupuestado: string | null; celdas: CeldaComparacion[] }[];
  /** Por columna: las líneas que no van a ítems de este rubro. */
  otras: (string | null)[];
  /** Por columna: la suma de todas sus líneas. */
  totales: (string | null)[];
}

export interface CeldaComparacion {
  usd: string | null;
  /** usd − presupuestado. */
  diferencia: string | null;
}

/** Resultado de importar la planilla de cómputo (con `confirmado: false`, la vista previa). */
export interface ImportacionPresupuesto {
  confirmado: boolean;
  rubros_nuevos: string[];
  rubros_existentes: string[];
  rubros_salteados: string[];
  rubros_archivados: string[];
  items: number;
  subtitulos: string[];
}

export interface Presupuesto {
  items: ItemPresupuesto[];
  totales: { presupuesto: string; facturado: string; pagado: string; disponible: string; facturado_sin_presupuesto: string };
  /** ARS por USD con que se pasan a USD los precios en pesos; null = sin cargar. */
  tipo_cambio: string | null;
  /** Ítems con los precios todavía en USD: se pasan a pesos con el primer tipo de cambio. */
  items_en_usd: number;
}

// ---- Avance de obra

export type EstadoTarea = "sin_empezar" | "en_curso" | "terminada";

export interface ItemRecorrido {
  etapa_id: number | null; // el id del ítem del presupuesto; null en un sub-hito
  hito_id?: number | null; // sólo sub-hitos (recorrido por hitos de Configuración)
  tipo: "tarea" | "hito" | "subhito";
  nombre: string;
  descripcion: string | null;
  estado: EstadoTarea;
  fecha: string | null; // terminada: cuándo; en curso: desde cuándo; sin empezar: null
  clase?: ClaseTarea | null; // sub-hitos del recorrido del desarrollo
  no_corresponde?: boolean; // cuenta como hecho, sin fecha
  automatico?: boolean; // se tilda solo (la firma del terreno)
}

/** El tipo de una tarea del recorrido del desarrollo (socio de Mati, 2026-11-03). */
export type ClaseTarea = "hito" | "tramite" | "pago" | "obra";

export type TipoPasoRecorrido = "terreno" | "rubro" | "hito" | "grupo" | "fin";

/** Un paso grande del recorrido de la obra (la firma del terreno, un rubro del cronograma, un hito o el final). */
export interface PasoRecorrido {
  clave: string; // "terreno" | "rubro-<id>" | "hito-<id>" | "grupo-<id>" | "fin"
  tipo: TipoPasoRecorrido;
  nombre: string;
  descripcion: string | null;
  estado: EstadoTarea;
  porcentaje: number | null; // sólo rubros y grupos, entero truncado
  fecha: string | null; // sólo fechas reales: cuándo terminó o desde cuándo está en curso
  etapa_id: number | null; // hitos: el id del ítem
  items: ItemRecorrido[]; // rubros: sus tareas; grupos: sus sub-hitos
  fase?: string | null; // recorrido del desarrollo: Desarrollo, Proyecto, Permisos, Obra, Cierre
  responsables?: string | null;
}

export interface CompletadoObra {
  porcentaje: number; // entero, truncado
  terminadas: number;
  en_curso: number;
  total: number;
}

/** Lo que reciben contador, escribano y fiduciante. */
export interface AvanceLector {
  completado: CompletadoObra;
  recorrido: PasoRecorrido[];
  /** "hitos": hay hitos cargados en Configuración → Avance de obra y el recorrido y el % salen de ellos. */
  fuente: "cronograma" | "hitos";
}

/** Un sub-hito de Configuración → Avance de obra. Completado = tiene la novedad que lo anunció. */
export interface SubHito {
  id: number;
  nombre: string;
  descripcion: string | null;
  completado_el: string | null;
  novedad_id: number | null;
  clase?: ClaseTarea | null;
  no_corresponde?: boolean;
  automatico?: string | null;
}

/** Configuración → Mensajes a proveedores (`GET /mensajes-proveedores`): el texto de cada mensaje y el nombre de cada
 * variable propia son de todos los desarrollos; el valor de una variable propia, de éste. */
export interface VariableProyecto {
  nombre: string;
  valor: string | null; // null = todavía no está cargado ("Proveedor" siempre: es el nombre de a quién se le escribe)
}

export interface VariablePropia {
  id: number;
  nombre: string;
  valor: string | null;
}

export interface MensajeProveedor {
  id: number;
  etiqueta_id: number | null; // null = el general, para las etiquetas sin mensaje propio
  etiqueta: string | null;
  texto: string;
}

export interface MensajesProveedores {
  automaticas: VariableProyecto[];
  propias: VariablePropia[];
  mensajes: MensajeProveedor[]; // el general primero
  etiquetas: Etiqueta[];
}

/** Un hito de Configuración → Avance de obra (ej. "Trabajos preliminares y cimentación") con sus sub-hitos en orden. */
export interface HitoObra {
  id: number;
  nombre: string;
  descripcion: string | null;
  sub: SubHito[];
  fase?: string | null;
  responsables?: string | null;
}

export interface ResumenAvance {
  real: string;
  planificado: string | null; // null = sin fecha de inicio
  diferencia: string | null;
}

/** Un rubro del cronograma con tareas, en el control interno del admin. */
export interface RubroAvance {
  id: number;
  nombre: string;
  inicio_plan: string | null;
  fin_plan: string | null;
  avance: string;
}

/** Un ítem del presupuesto con duración ≥ 1 (una tarea del cronograma). */
export interface TareaAvance {
  id: number;
  nombre: string;
  rubro_id: number;
  duracion: number;
  peso: string;
  inicio_plan: string | null;
  fin_plan: string | null;
  planificado: string | null;
  estado: EstadoTarea;
  atrasada: boolean;
  tiene_avances: boolean;
}

/** Un ítem de 0 días del cronograma: se cumple cuando terminan las tareas de las que depende. */
export interface HitoAvance {
  id: number;
  nombre: string;
  rubro_id: number;
  fecha_plan: string | null;
  cumplido_el: string | null;
}

export interface PuntoCurva {
  fecha: string;
  planificado: string;
  real: string | null; // null para fechas posteriores a hoy
}

export interface AvanceAdmin extends AvanceLector {
  inicio: string | null;
  fin_estimado: string | null;
  resumen: ResumenAvance | null; // null = ningún ítem tiene duración en el cronograma
  rubros: RubroAvance[];
  tareas: TareaAvance[];
  hitos: HitoAvance[];
  curva: PuntoCurva[];
  cronograma_adjuntos: Adjunto[];
}

export type Avance = AvanceLector | AvanceAdmin;

export interface AvanceNovedad {
  etapa_id: number;
  etapa: string;
  anterior: EstadoTarea;
  estado: EstadoTarea;
}

export interface FotoNovedad {
  id: number;
  orden: number;
  ancho: number;
  alto: number;
  epigrafe: string | null;
}

export interface Novedad {
  id: number;
  fecha: string;
  titulo: string;
  texto: string | null;
  avances: AvanceNovedad[];
  fotos: FotoNovedad[];
  creado_por: string;
  creado_en: string;
}

export interface ListadoNovedades {
  items: Novedad[];
  hay_mas: boolean;
}

// ---- Novedades del emprendimiento: el Inicio del fiduciante (Mati, 2026-10-19)

export type TipoNoticia = "obra" | "gasto" | "pago" | "terreno" | "prevision" | "venta";

export interface Noticia {
  clave: string;
  tipo: TipoNoticia;
  fecha: string;
  titulo: string;
  texto: string | null;
  /** Sólo en las novedades de obra. */
  fotos: FotoNovedad[];
  /** Gasto, pago y pago a cuenta del terreno: la novedad abre la ficha de ese gasto. */
  gasto_id: number | null;
  creado_en: string;
}

export interface ListadoNoticias {
  items: Noticia[];
  hay_mas: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    mensaje: string,
  ) {
    super(mensaje);
    this.name = "ApiError";
  }
}

const MENSAJE_RED = "No se pudo conectar con el servidor. Revisá tu conexión.";

function mensajeDeError(status: number, cuerpo: unknown): string {
  const detalle = (cuerpo as { detail?: unknown } | null)?.detail;
  if (typeof detalle === "string") return detalle;
  if (Array.isArray(detalle)) return "Revisá los datos ingresados.";
  if (status === 413 && typeof detalle !== "string") return "El archivo supera los 10 MB";
  if (status >= 500) return "Error del servidor. Probá de nuevo en un rato.";
  return `Error ${status}`;
}

/** Único endpoint que responde 401 por otra cosa que la sesión (contraseña incorrecta). */
const RUTA_LOGIN = "/api/auth/login";

let alVencer: (() => void) | null = null;

/** Registra qué hacer cuando la API responde 401 (sesión vencida o revocada). Devuelve cómo desregistrarlo. */
export function alVencerSesion(fn: () => void): () => void {
  alVencer = fn;
  return () => {
    if (alVencer === fn) alVencer = null;
  };
}

function avisarSiVencio(status: number, ruta: string) {
  if (status === 401 && ruta !== RUTA_LOGIN) alVencer?.();
}

export interface Descarga {
  blob: Blob;
  nombre: string;
}

/** "attachment; filename=\"caja_2026-08-01_2026-08-31.csv\"" → el nombre entre comillas, o "descarga" si falta. */
function nombreDeDisposicion(cd: string | null): string {
  const m = cd ? /filename="?([^";]+)"?/.exec(cd) : null;
  return m?.[1] ?? "descarga";
}

/** Descarga un archivo (CSV/ZIP de reportes): arma el Blob a mano desde `arrayBuffer()` (no `.blob()`, ver
 * docs/testing.md sobre el shim de fetch/Blob de vitest en jsdom) y toma el nombre de `Content-Disposition`. */
async function descargar(ruta: string): Promise<Descarga> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`${API}${ruta}`, { credentials: "same-origin" });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, MENSAJE_RED);
  }
  if (!respuesta.ok) {
    avisarSiVencio(respuesta.status, ruta);
    const datos: unknown = await respuesta.json().catch(() => null);
    throw new ApiError(respuesta.status, mensajeDeError(respuesta.status, datos));
  }
  const buffer = await respuesta.arrayBuffer();
  const tipo = respuesta.headers.get("Content-Type");
  const blob = new Blob([buffer], tipo ? { type: tipo } : undefined);
  return { blob, nombre: nombreDeDisposicion(respuesta.headers.get("Content-Disposition")) };
}

async function request<T>(metodo: string, ruta: string, cuerpo?: unknown, signal?: AbortSignal): Promise<T> {
  const esForm = cuerpo instanceof FormData;
  let respuesta: Response;
  try {
    respuesta = await fetch(`${API}${ruta}`, {
      method: metodo,
      credentials: "same-origin",
      headers: cuerpo === undefined || esForm ? undefined : { "Content-Type": "application/json" },
      body: cuerpo === undefined ? undefined : esForm ? cuerpo : JSON.stringify(cuerpo),
      signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, MENSAJE_RED);
  }
  if (respuesta.status === 204) return undefined as T;
  const datos: unknown = await respuesta.json().catch(() => null);
  if (!respuesta.ok) {
    avisarSiVencio(respuesta.status, ruta);
    throw new ApiError(respuesta.status, mensajeDeError(respuesta.status, datos));
  }
  return datos as T;
}

export const api = {
  get: <T>(ruta: string, signal?: AbortSignal) => request<T>("GET", ruta, undefined, signal),
  post: <T>(ruta: string, cuerpo?: unknown) => request<T>("POST", ruta, cuerpo),
  patch: <T>(ruta: string, cuerpo: unknown) => request<T>("PATCH", ruta, cuerpo),
  put: <T>(ruta: string, cuerpo: unknown) => request<T>("PUT", ruta, cuerpo),
  delete: <T>(ruta: string) => request<T>("DELETE", ruta),
  /** Multipart: no fija Content-Type (el browser pone el boundary). */
  upload: <T>(ruta: string, form: FormData) => request<T>("POST", ruta, form),
  descargar,
};

// ------------------------------------------------------------------ reparto de la ganancia (clase A)

export interface RepartoGanancia {
  fecha: string;
  monto_usd: string;
  nota: string | null;
  /** null para el fiduciante (no ve las cajas). */
  caja_id: number | null;
}

export interface FilaGanancia {
  fiduciante_id: number;
  nombre: string;
  /** Su % dentro de los clase A ("12.5000"). */
  porcentaje_a: string;
  aportado_usd: string;
  /** Lo que le tocó del reparto o, si todavía no se repartió, lo que le tocaría hoy. */
  cobrado_usd: string;
  ganancia_usd: string;
  /** Ganancia / aportado × 100 ("25.00"); null sin aportes. */
  rentabilidad_pct: string | null;
}

export interface TotalesGanancia {
  aportado_usd: string;
  cobrado_usd: string;
  ganancia_usd: string;
  rentabilidad_pct: string | null;
}

export interface Ganancia {
  reparto: RepartoGanancia | null;
  disponible_ventas_usd: string;
  filas: FilaGanancia[];
  totales: TotalesGanancia;
}

export interface PayloadReparto {
  fecha: string;
  monto_usd: string;
  nota: string | null;
  caja_id?: number;
}

// ------------------------------------------------------------------ honorarios de la fiduciaria

/** Los fees de un canal (A o B) en una moneda. */
export interface CanalHonorarios {
  tipo: TipoGasto;
  moneda: Moneda;
  generado: string;
  ganado: string;
  /** A: liquidado (o pagado a mano); B: acreditado. */
  cobrado: string;
  pendiente: string;
}

export interface BeneficiarioFeeB {
  fiduciante_id: number;
  nombre: string;
  porcentaje: string;
  acreditado_ars: string;
  acreditado_usd: string;
  acreditado_total_usd: string;
}

export interface LiquidacionFee {
  id: number;
  /** AAAA-MM */
  mes: string;
  moneda: Moneda;
  factura_nro: string;
  fecha: string;
  caja_id: number;
  caja: string;
  monto: string;
  tipo_cambio: string | null;
  monto_usd: string;
  cantidad_fees: number;
  adjuntos: Adjunto[];
}

export interface Honorarios {
  fee_porcentaje: string;
  /** A y B, en ARS y USD (siempre los cuatro). */
  canales: CanalHonorarios[];
  beneficiarios: BeneficiarioFeeB[];
  /** La más nueva primero. */
  liquidaciones: LiquidacionFee[];
}

export type EstadoFee = "por_cobrar" | "cobrado" | "por_acreditar" | "acreditado";

/** Una fila del registro de fees (`GET /honorarios/registro`, Mati, 2026-11-02): un fee generado por el pago que saldó al proveedor. */
export interface FeeGenerado {
  fee_id: number;
  /** El gasto que lo generó. */
  gasto_id: number;
  tipo: TipoGasto;
  /** La del pago que saldó al proveedor. */
  fecha: string;
  proveedor_id: number | null;
  proveedor: string | null;
  descripcion: string;
  comprobante_nro: string | null;
  monto_gasto: string;
  porcentaje: string | null;
  fee: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  /** Los tres en USD al tipo de cambio del pago que saldó (cierran). */
  fee_usd: string;
  /** A: liquidado (o pagado a mano); B: acreditado. */
  cobrado: string;
  cobrado_usd: string;
  por_cobrar: string;
  por_cobrar_usd: string;
  estado: EstadoFee;
  liquidaciones: { id: number; factura_nro: string; fecha: string }[];
  acreditado_a: { fiduciante_id: number; nombre: string; monto: string }[];
}

export interface RegistroFees {
  /** El más nuevo primero. */
  items: FeeGenerado[];
}

export interface PayloadBeneficiarios {
  beneficiarios: { fiduciante_id: number; porcentaje: string }[];
}

export interface PayloadLiquidacion {
  /** Los fees A por cobrar elegidos en el registro (de una sola moneda). */
  fee_ids: number[];
  factura_nro: string;
  fecha: string;
  caja_id?: number;
  tipo_cambio: string | null;
}

/** Cash flow mensual (`GET /cash-flow`, sólo admin): en USD; `ars` es lo que se paga en pesos, aparte (no suma al total). */
export interface ImporteCashFlow {
  comprometido_materiales: string;
  comprometido_mano_obra: string;
  estimado_materiales: string;
  estimado_mano_obra: string;
  fee: string;
  total: string;
  ars: string;
}

export interface RubroMesCashFlow extends ImporteCashFlow {
  /** null: pagos de presupuestos que no cubren ningún ítem ("Sin rubro"). */
  rubro_id: number | null;
  rubro: string;
}

export interface MesCashFlow extends ImporteCashFlow {
  /** "2026-11" */
  mes: string;
  acumulado: string;
  rubros: RubroMesCashFlow[];
}

export interface SinFechaCashFlow extends ImporteCashFlow {
  origen: "comprometido" | "estimado";
  cotizacion_id: number | null;
  proveedor: string | null;
  etiqueta: string | null;
  concepto: string | null;
  item_id: number | null;
  rubro: string | null;
  descripcion: string | null;
  motivo: string;
}

export interface SinPresupuestoCashFlow {
  item_id: number;
  rubro_id: number;
  rubro: string;
  descripcion: string;
  unidad: string | null;
  cantidad: string | null;
}

export interface CashFlow {
  fee_porcentaje: string;
  inicio: string | null;
  meses: MesCashFlow[];
  total: ImporteCashFlow;
  sin_fecha: SinFechaCashFlow[];
  total_sin_fecha: ImporteCashFlow;
  sin_presupuesto: SinPresupuestoCashFlow[];
  avisos: { cotizacion_id: number; texto: string }[];
}
